'use client';

import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useMemo } from 'react';
import { orderStateMachine, validateOrderData, orderSelectors } from '../state-machines/orderStateMachine';
import { OrderSubmissionService } from '../services/OrderSubmissionService';
import { OrderAdapter } from '../adapters/OrderAdapter';
import { useProductRepository, useOrderRepository } from '@/core/infrastructure/hooks/useRepositories';
import type { OrderReadyToSubmit } from '@/components/order/order.types';

/**
 * React hook for managing order workflow with state machine
 * Provides complete order submission process with validation, error handling, and retry logic
 */
export function useOrderWorkflow() {
  const [state, send] = useMachine(orderStateMachine);
  const productRepository = useProductRepository();
  const orderRepository = useOrderRepository();

  const submissionService = useMemo(
    () => new OrderSubmissionService(orderRepository, productRepository),
    [orderRepository, productRepository],
  );

  const orderAdapter = useMemo(() => new OrderAdapter(productRepository), [productRepository]);

  // Auto-start validation when order data changes
  useEffect(() => {
    if (orderSelectors.isValidated(state) && state.context.orderData) {
      const validation = validateOrderData(state.context.orderData);
      if (!validation.isValid) {
        send({ type: 'VALIDATE' });
      }
    }
  }, [state.context.orderData, send, state]);

  // Actions
  const startOrder = useCallback(
    (orderData: OrderReadyToSubmit) => {
      send({ type: 'START_ORDER', orderData });
    },
    [send],
  );

  const validateOrder = useCallback(() => {
    send({ type: 'VALIDATE' });
  }, [send]);

  const submitOrder = useCallback(() => {
    send({ type: 'SUBMIT' });
  }, [send]);

  const processPayment = useCallback(() => {
    send({ type: 'PROCESS_PAYMENT' });
  }, [send]);

  const confirmOrder = useCallback(() => {
    send({ type: 'CONFIRM_ORDER' });
  }, [send]);

  const cancelOrder = useCallback(
    (reason?: string) => {
      send({ type: 'CANCEL_ORDER', reason });
    },
    [send],
  );

  const retry = useCallback(() => {
    send({ type: 'RETRY' });
  }, [send]);

  const reset = useCallback(() => {
    send({ type: 'RESET' });
  }, [send]);

  // Selectors
  const selectors = {
    isIdle: orderSelectors.isIdle(state),
    isValidating: orderSelectors.isValidating(state),
    isInvalid: orderSelectors.isInvalid(state),
    isValidated: orderSelectors.isValidated(state),
    isSubmitting: orderSelectors.isSubmitting(state),
    isSubmitFailed: orderSelectors.isSubmitFailed(state),
    isSubmitted: orderSelectors.isSubmitted(state),
    isProcessingPayment: orderSelectors.isProcessingPayment(state),
    isPaymentFailed: orderSelectors.isPaymentFailed(state),
    isConfirmed: orderSelectors.isConfirmed(state),
    isCancelled: orderSelectors.isCancelled(state),

    canSubmit: orderSelectors.canSubmit(state),
    canRetry: orderSelectors.canRetry(state),
    canCancel: orderSelectors.canCancel(state),

    hasError: orderSelectors.hasError(state),
    hasValidationErrors: orderSelectors.hasValidationErrors(state),

    isLoading: orderSelectors.isLoading(state),
    isComplete: orderSelectors.isComplete(state),
  };

  return {
    // State
    state: state.value,
    context: state.context,

    // Selectors
    ...selectors,

    // Actions
    startOrder,
    validateOrder,
    submitOrder,
    processPayment,
    confirmOrder,
    cancelOrder,
    retry,
    reset,

    // Services
    submissionService,
    orderAdapter,

    // Raw state for advanced usage
    machineState: state,
    send,
  };
}

/**
 * Hook for order workflow status with simplified interface
 */
export function useOrderStatus() {
  const workflow = useOrderWorkflow();

  const getStatusMessage = (): string => {
    if (workflow.isIdle) return 'Ready to start';
    if (workflow.isValidating) return 'Validating order...';
    if (workflow.isInvalid) return 'Please fix validation errors';
    if (workflow.isValidated) return 'Order validated, ready to submit';
    if (workflow.isSubmitting) return 'Submitting order...';
    if (workflow.isSubmitFailed) return 'Failed to submit order';
    if (workflow.isSubmitted) return 'Order submitted successfully';
    if (workflow.isProcessingPayment) return 'Processing payment...';
    if (workflow.isPaymentFailed) return 'Payment failed';
    if (workflow.isConfirmed) return 'Order confirmed!';
    if (workflow.isCancelled) return 'Order cancelled';
    return 'Unknown status';
  };

  const getStatusType = (): 'idle' | 'loading' | 'success' | 'error' | 'warning' => {
    if (workflow.isLoading) return 'loading';
    if (workflow.isConfirmed) return 'success';
    if (workflow.hasError || workflow.isInvalid || workflow.isSubmitFailed || workflow.isPaymentFailed) return 'error';
    if (workflow.isCancelled) return 'warning';
    return 'idle';
  };

  return {
    ...workflow,
    statusMessage: getStatusMessage(),
    statusType: getStatusType(),
    progress: workflow.isConfirmed
      ? 100
      : workflow.isSubmitted
        ? 75
        : workflow.isValidated
          ? 50
          : workflow.isValidating
            ? 25
            : 0,
  };
}
